import { NavLink } from 'react-router-dom';
import { LayoutTemplate, Users, FileSpreadsheet, BarChart3, UserCog } from 'lucide-react';
import { UserRole } from '@pay-plan/shared';
import { useAuth } from '../context/AuthContext.js';
import darLogo from '../assets/darlings-logo.png';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  roles: UserRole[];
}

const NAV_ITEMS: NavItem[] = [
  {
    to: '/templates',
    label: 'Templates',
    icon: <LayoutTemplate size={20} />,
    // GM and Accounting see templates read-only; only Admin/Executive can edit.
    roles: [
      UserRole.Admin,
      UserRole.Executive,
      UserRole.AccountingManager,
      UserRole.GeneralManager,
    ],
  },
  {
    to: '/assignments',
    label: 'Assignments',
    icon: <Users size={20} />,
    // GM is read-only and has no assignment workflow, so the nav entry is hidden.
    roles: [UserRole.Admin, UserRole.Executive, UserRole.AccountingManager],
  },
  {
    to: '/pay-sheets',
    label: 'Pay Sheets',
    icon: <FileSpreadsheet size={20} />,
    roles: [
      UserRole.Admin,
      UserRole.Executive,
      UserRole.AccountingManager,
      UserRole.GeneralManager,
    ],
  },
  {
    to: '/reports',
    label: 'Reports',
    icon: <BarChart3 size={20} />,
    roles: [
      UserRole.Admin,
      UserRole.Executive,
      UserRole.AccountingManager,
      UserRole.GeneralManager,
    ],
  },
  {
    to: '/users',
    label: 'User Management',
    icon: <UserCog size={20} />,
    roles: [UserRole.Admin, UserRole.Executive, UserRole.AccountingManager],
  },
];

export function Sidebar(): React.JSX.Element {
  const { user, role } = useAuth();

  const visibleItems = NAV_ITEMS.filter((item) => role !== null && item.roles.includes(role));

  return (
    <aside className="w-64 h-screen fixed left-0 top-0 bg-brand text-white flex flex-col">
      <div className="px-6 py-5 border-b border-white/10">
        <img src={darLogo} alt="Darling's Auto Group" className="h-8" />
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-white/15 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      {user !== null && (
        <div className="px-6 py-4 border-t border-white/10">
          <p className="text-sm font-medium truncate">{user.displayName}</p>
          <p className="text-xs text-white/50 capitalize">{role?.replace('_', ' ')}</p>
        </div>
      )}
    </aside>
  );
}
